import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  GraduationCap, Wallet, PlaneTakeoff, MapPin, Briefcase,
  ShieldCheck, ArrowRight, Sparkles, Lock,
} from "lucide-react";

const LAYERS = [
  {
    icon: GraduationCap,
    title: "Academic fit",
    body: "Can you realistically get in? We translate grades across A-Level / IB / CBSE / American Diploma and score you against the *typical* admitted student — not the published minimum.",
    tag: "Aspirational · Competitive · Realistic · Secure",
  },
  {
    icon: Wallet,
    title: "Financial fit",
    body: "Total degree cost, not just tuition. Includes accommodation, food, insurance, visa, travel and expected fee growth. Filters against your family's real annual and total budget — and accounts for likely scholarships.",
    tag: "In-budget · Near budget · Out of budget",
  },
  {
    icon: PlaneTakeoff,
    title: "Visa readiness",
    body: "Passport + residence-aware. Checks sponsor licence, financial-evidence period, blocked-account rules (Germany), interview requirements (US), TB testing, security-clearance for sensitive subjects, and refusal-history complications.",
    tag: "Ready · Prep needed · Not currently feasible",
  },
  {
    icon: MapPin,
    title: "Personal fit",
    body: "Your weighted preferences across prestige, campus feel, city size, diversity, sport, distance from home, climate. Explains *why* the university matches — never a mystery percentage.",
    tag: "Weighted scoring with per-uni reasoning",
  },
  {
    icon: Briefcase,
    title: "Career-value fit",
    body: "Accreditation, placement year availability, graduate employment rate, starting salary, top employers, post-study work rights — and whether the qualification is recognised in your target work country.",
    tag: "Recognition · Placement · PSW · Salary",
  },
];

export default function AdvancedTeaser({ onStartFreeTrial }) {
  const { login } = useAuth();
  return (
    <div>
      {/* HERO */}
      <section className="border-b border-stone-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-16">
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-8">
              <div className="label-uppercase text-[#4C1D95]">Advanced Search · 3 free matches, no sign-up</div>
              <h1 className="mt-4 font-editorial text-4xl sm:text-5xl lg:text-6xl leading-[0.98] tracking-tight">
                A university isn't a genuine match unless you can{" "}
                <span className="italic">get in</span>,{" "}
                <span className="italic">afford it</span>, and{" "}
                <span className="italic">obtain the visa</span>.
              </h1>
              <p className="mt-6 text-lg text-stone-600 leading-relaxed max-w-3xl">
                The Quick Check answers "can I get in?" alone. Advanced Search runs
                every university through five transparent matching layers,
                surfaces hard gates and preparation warnings, and produces a{" "}
                <span className="text-stone-900 font-medium">strategically-balanced portfolio</span>{" "}
                — not one unexplained percentage.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                {onStartFreeTrial ? (
                  <Button
                    data-testid="advanced-free-trial-btn"
                    onClick={onStartFreeTrial}
                    size="lg"
                    className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
                  >
                    <Sparkles className="h-4 w-4 mr-2" />
                    Try 3 free matches — no sign-up
                  </Button>
                ) : null}
                <Button
                  data-testid="advanced-signin-btn"
                  onClick={login}
                  size="lg"
                  variant="outline"
                  className="rounded-md"
                >
                  Sign in for unlimited matches
                </Button>
                <a href="/admission" className="inline-block">
                  <Button variant="ghost" size="lg" className="rounded-md">
                    Try the free Quick Check first
                  </Button>
                </a>
                <a href="/flagships" className="inline-block">
                  <Button
                    data-testid="advanced-flagship-preset-btn"
                    variant="ghost"
                    size="lg"
                    className="rounded-md text-emerald-800 hover:bg-emerald-50"
                  >
                    <ShieldCheck className="h-4 w-4 mr-2 text-emerald-700" />
                    Browse the Flagship 50+
                  </Button>
                </a>
              </div>
              <div className="mt-6 flex items-center gap-2 text-xs text-stone-500 font-mono">
                <Lock className="h-3 w-3" />
                Free trial keeps everything client-side — nothing is saved to an account until you sign in.
              </div>
            </div>
            <div className="lg:col-span-4">
              <Card className="border-stone-200 p-6 bg-[#F3F1EC]">
                <div className="label-uppercase">Central principle</div>
                <p className="mt-3 font-editorial text-xl leading-snug">
                  "A university is not a genuine match unless the student is
                  academically eligible, can afford it, and has a credible
                  route to obtaining permission to study there."
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* 5 LAYERS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="label-uppercase">What Advanced Search will do</div>
        <h2 className="font-editorial text-3xl sm:text-4xl mt-3 max-w-3xl">
          Five transparent scores per university. No mystery percentages.
        </h2>
        <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-5 gap-4">
          {LAYERS.map((L, i) => (
            <Card key={i} data-testid={`layer-card-${i}`} className="p-5 border-stone-200 bg-white lift">
              <L.icon className="h-6 w-6 text-[#4C1D95]" />
              <h3 className="font-editorial text-xl mt-4 leading-tight">{L.title}</h3>
              <p className="mt-3 text-sm text-stone-600 leading-relaxed">{L.body}</p>
              <div className="mt-4 label-uppercase !text-[9px]">{L.tag}</div>
            </Card>
          ))}
        </div>
      </section>

      {/* SAMPLE CARD */}
      <section className="bg-[#F3F1EC] border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <div className="label-uppercase">A useful result card</div>
              <h2 className="font-editorial text-3xl sm:text-4xl mt-3">
                Each match answers six questions immediately.
              </h2>
              <ul className="mt-6 space-y-3 text-stone-700">
                <li>· Academic fit — with a Realistic/Competitive/Aspirational bucket</li>
                <li>· Financial fit — with a concrete gap or surplus figure</li>
                <li>· Visa readiness — with what still needs preparing</li>
                <li>· Personal fit — with your top matching factors called out</li>
                <li>· Career fit — accreditation, placement, PSW, salary</li>
                <li>· Your next action — one sentence, dated where possible</li>
              </ul>
            </div>
            <Card className="p-6 border-stone-200 bg-white shadow-sm">
              <div className="flex items-baseline justify-between">
                <div className="font-editorial text-2xl">University of Example</div>
                <div className="font-chip text-3xl font-black text-[#4C1D95]">86%</div>
              </div>
              <div className="label-uppercase mt-1">Overall match</div>
              <div className="mt-5 space-y-2 text-sm">
                {[
                  ["Academic fit", "91", "Your predicted grades are above the usual entry range."],
                  ["Financial fit", "78", "First-year cost is $4,500 above budget; a $5k scholarship would close the gap."],
                  ["Visa readiness", "74", "Approved sponsor. Financial evidence needs preparing."],
                  ["Personal fit", "89", "Large international community, city campus, strong societies."],
                  ["Career fit", "92", "Course is professionally accredited with an optional placement year."],
                ].map(([k, s, note]) => (
                  <div key={k} className="grid grid-cols-[110px_50px_1fr] items-start gap-3">
                    <div className="label-uppercase !text-[10px]">{k}</div>
                    <div className="font-mono font-semibold text-stone-900">{s}%</div>
                    <div className="text-xs text-stone-600">{note}</div>
                  </div>
                ))}
              </div>
              <div className="mt-5 pt-4 border-t border-stone-200 text-sm">
                <div className="label-uppercase">Your next action</div>
                <p className="mt-1 text-stone-800">Take the required English test by 15 Oct and begin holding the visa funds no later than 1 Nov.</p>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* GATES / WARNINGS / POSITIVES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="label-uppercase">Visa is treated as part of the shortlist</div>
        <h2 className="font-editorial text-3xl sm:text-4xl mt-3 max-w-3xl">
          Three kinds of decision, per university.
        </h2>
        <div className="mt-8 grid md:grid-cols-3 gap-4">
          <Card className="p-6 border-l-4 border-red-700 bg-red-50">
            <div className="label-uppercase text-red-800">Hard gates</div>
            <p className="mt-3 text-sm text-stone-800">
              Not licensed to sponsor. Budget nowhere near required. Dependants not allowed
              on that route. Deadline passed. Course not open to internationals.
              → Removed from the principal shortlist and shown under "Not currently feasible".
            </p>
          </Card>
          <Card className="p-6 border-l-4 border-amber-700 bg-amber-50">
            <div className="label-uppercase text-amber-800">Preparation warnings</div>
            <p className="mt-3 text-sm text-stone-800">
              Funds need to be held longer. Language test still to take. Residence permission
              expires too soon. Prior refusal needs specialist review. TB test, security
              clearance or Campus France interview required. → Uni stays, action listed.
            </p>
          </Card>
          <Card className="p-6 border-l-4 border-emerald-700 bg-emerald-50">
            <div className="label-uppercase text-emerald-800">Positive factors</div>
            <p className="mt-3 text-sm text-stone-800">
              Clear international guidance. Responsive visa team. Fast CAS/I-20 issuance.
              Transparent deposit-refund policy. Pre-arrival briefings. Strong support
              for students from your country. → Boosts support score, never the match itself.
            </p>
          </Card>
        </div>
      </section>

      {/* PORTFOLIO & WUA PLUS */}
      <section className="bg-white border-t border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-2 gap-10">
          <Card className="p-6 border-stone-200">
            <div className="label-uppercase">Balanced application portfolio</div>
            <h3 className="font-editorial text-2xl mt-2">Not the ten highest scores — a strategy.</h3>
            <ul className="mt-4 text-sm text-stone-700 space-y-2">
              <li>· Two aspirational choices</li>
              <li>· Three competitive choices</li>
              <li>· Three realistic choices</li>
              <li>· Two secure choices</li>
            </ul>
            <p className="mt-4 text-sm text-stone-600">
              You'll also be warned if your list depends on one high-risk visa route,
              or on a single deposit deadline you can't miss.
            </p>
          </Card>
          <Card className="p-6 border-stone-200 bg-[#F3F1EC]">
            <div className="label-uppercase flex items-center gap-2"><ShieldCheck className="h-3 w-3" /> WUA+</div>
            <h3 className="font-editorial text-2xl mt-2">When judgement matters, we escalate.</h3>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed">
              Prior visa refusals, unusual nationality/residence combinations, complex sponsors,
              dependants, under-18 applicants, guardianship, study gaps, immigration
              breaches or tight deadlines — Advanced Search will surface the trigger
              and offer a hand-holding consultation with WUA+.
            </p>
            <p className="mt-3 text-sm text-stone-700 leading-relaxed">
              Documents (transcripts, passports, financial evidence, offers, visa milestones)
              live in the optional <span className="font-semibold">Pearsana</span> student wallet.
              Your WUA profile can be handed off at any time.
            </p>
          </Card>
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pb-16 flex flex-wrap gap-3">
          {onStartFreeTrial && (
            <Button
              data-testid="advanced-free-trial-btn-bottom"
              onClick={onStartFreeTrial}
              size="lg"
              className="bg-[#4C1D95] hover:bg-[#3B0764] text-white rounded-md"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Try 3 free matches — no sign-up
            </Button>
          )}
          <Button
            data-testid="advanced-signin-btn-bottom"
            onClick={login}
            variant="outline"
            size="lg"
            className="rounded-md"
          >
            Sign in for unlimited <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>
    </div>
  );
}
